import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, CheckCircle2, Lock, Mail, AlertCircle } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isResetPath = location.pathname === "/reset-password";
  const tokenFromUrl = searchParams.get("token");

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
  }, [location.pathname]);

  const handleRequestResetLink = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setRequestSubmitted(true);
      if (data.debug) {
        setDebugInfo(data.debug);
      }
      toast.success("Password reset request processed.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    if (!tokenFromUrl) {
      setError("Missing reset token. Please use the link sent to your email.");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token: tokenFromUrl,
        new_password: newPassword,
      });
      toast.success(data.message || "Password updated successfully! Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Rendering for /reset-password page
  if (isResetPath) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center p-6 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center pointer-events-none"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        />
        
        <Link to="/" className="absolute top-8 left-1/2 -translate-x-1/2 flex items-center gap-3 group">
          <div className="w-10 h-10 rounded bg-[#FF3B30] flex items-center justify-center text-white font-black text-xl shadow-[0_0_15px_rgba(255,59,48,0.5)] group-hover:scale-105 transition-transform">
            CP
          </div>
          <h1 className="text-2xl font-black uppercase tracking-wider text-white">CRICKETPULSE</h1>
        </Link>

        <div className="relative z-10 w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl p-8 shadow-2xl space-y-6">
          {!tokenFromUrl ? (
            <div className="space-y-4 text-center py-4">
              <div className="w-12 h-12 rounded-full bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] flex items-center justify-center mx-auto">
                <AlertCircle className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-black uppercase text-white">Invalid Reset Link</h2>
              <p className="text-sm text-[#A3A3A3]">
                This password reset link is invalid or missing authorization parameters. Please request a new link.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block bg-[#FF3B30] hover:bg-[#E03229] text-white font-bold px-6 py-2.5 rounded text-xs uppercase tracking-wider transition-all"
              >
                Request New Link
              </Link>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded px-2.5 py-1">
                  <KeyRound className="w-3 h-3" /> Set New Password
                </div>
                <h2 className="text-3xl font-black uppercase text-white">Choose New Password</h2>
                <p className="text-sm text-[#A3A3A3]">
                  Enter your new secure password below to update your account.
                </p>
              </div>

              {error && (
                <div data-testid="reset-password-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] text-xs rounded px-3.5 py-2.5">
                  {error}
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-mono text-[#A3A3A3] uppercase mb-1 flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5 text-[#FF3B30]" /> New Password
                  </label>
                  <input
                    data-testid="new-password-input"
                    type="password"
                    required
                    minLength={6}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-[#262626] rounded px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF3B30]"
                    placeholder="At least 6 characters"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono text-[#A3A3A3] uppercase mb-1 flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5 text-[#FF3B30]" /> Confirm New Password
                  </label>
                  <input
                    data-testid="confirm-password-input"
                    type="password"
                    required
                    minLength={6}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-[#262626] rounded px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF3B30]"
                    placeholder="Re-enter new password"
                  />
                </div>

                <button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[#FF3B30] hover:bg-[#E03229] disabled:opacity-50 text-white font-bold py-2.5 rounded text-sm transition-all shadow-lg uppercase"
                >
                  {busy ? "Updating..." : "Save New Password"}
                </button>
              </form>
            </>
          )}

          <div className="border-t border-[#262626] pt-4 text-center">
            <Link
              to="/login"
              className="text-xs text-[#FF3B30] font-bold hover:underline inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Return to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Rendering for /forgot-password page
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center p-6 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-15 bg-cover bg-center pointer-events-none"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      />
      
      <Link to="/" className="absolute top-8 left-1/2 -translate-x-1/2 flex items-center gap-3 group">
        <div className="w-10 h-10 rounded bg-[#FF3B30] flex items-center justify-center text-white font-black text-xl shadow-[0_0_15px_rgba(255,59,48,0.5)] group-hover:scale-105 transition-transform">
          CP
        </div>
        <h1 className="text-2xl font-black uppercase tracking-wider text-white">CRICKETPULSE</h1>
      </Link>

      <div className="relative z-10 w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl p-8 shadow-2xl space-y-6">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded px-2.5 py-1">
            <KeyRound className="w-3 h-3" /> Password Recovery
          </div>
          <h2 className="text-3xl font-black uppercase text-white">Forgot Password?</h2>
          <p className="text-sm text-[#A3A3A3]">
            Enter your registered email address to receive a secure password reset link.
          </p>
        </div>

        {error && (
          <div data-testid="forgot-password-error" className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] text-xs rounded px-3.5 py-2.5">
            {error}
          </div>
        )}

        {requestSubmitted ? (
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 space-y-2">
              <div className="flex items-center gap-2 text-emerald-400 font-bold text-sm">
                <CheckCircle2 className="w-5 h-5 shrink-0" />
                <span>Reset Link Requested</span>
              </div>
              <p className="text-xs text-[#F5F5F5] leading-relaxed">
                If an account associated with <strong className="text-white">{email}</strong> exists, a password reset link has been sent to your email inbox.
              </p>
              <p className="text-xs text-[#A3A3A3]">
                Please check your inbox (and spam folder) and click the link in the email to set a new password.
              </p>
            </div>

            <button
              onClick={() => { setRequestSubmitted(false); setDebugInfo(null); }}
              className="w-full bg-[#1A1A1A] hover:bg-[#262626] text-[#A3A3A3] hover:text-white font-bold py-2 rounded text-xs transition-all uppercase font-mono border border-[#262626]"
            >
              Send Another Reset Link
            </button>
          </div>
        ) : (
          <form onSubmit={handleRequestResetLink} className="space-y-4">
            <div>
              <label className="block text-xs font-mono text-[#A3A3A3] uppercase mb-1 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-[#FF3B30]" /> Registered Email Address
              </label>
              <input
                data-testid="forgot-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#1A1A1A] border border-[#262626] rounded px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF3B30] transition-colors"
                placeholder="alex@cricket.com"
              />
            </div>

            <button
              data-testid="request-reset-btn"
              type="submit"
              disabled={busy}
              className="w-full bg-[#FF3B30] hover:bg-[#E03229] disabled:opacity-50 text-white font-bold py-2.5 rounded text-sm transition-all shadow-lg uppercase"
            >
              {busy ? "Sending Link..." : "Send Password Reset Link"}
            </button>
          </form>
        )}

        <div className="border-t border-[#262626] pt-4 text-center">
          <Link
            data-testid="back-to-login-link"
            to="/login"
            className="text-xs text-[#FF3B30] font-bold hover:underline inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
